import type { AxiosError, AxiosResponse } from 'axios'
import type { GetServerSideProps } from 'next'

import {
    CATEGORY_IDS,
    SUB_CATEGORY_FEATURED_EVENTS_PAGE_SIZE,
    SUB_CATEGORY_FEATURED_EVENTS_SORT_BY,
    URLS,
} from '@/constants'
import { replaceCmsTokens } from '@/context/cms/utils'
import { serverSideRead as serverSideReadPerformers } from '@/context/performer/context'
import type { PerformerDataType, PerformerParams } from '@/context/performer/types'
import { serverSideRead as serverSideReadProductionList } from '@/context/productions-list'
import { serverSideRead as serverSideReadSubCategory } from '@/context/sub-category/context'
import type { SubCategoryParams } from '@/context/sub-category/types'
import type {
    CardWithImageGroupsData,
    CmsSubCategory,
    LinkGroupPerformerData,
    Metadata,
    PerformerLink,
} from '@/types/app-types'

import type { SubCategoryPageProps } from '../types'

export const getServerSidePropsByCategoryId = (
    categoryId: CATEGORY_IDS,
    webPathOverride?: string,
): GetServerSideProps<SubCategoryPageProps> => {
    const getServerSideProps: GetServerSideProps<SubCategoryPageProps> = async (context) => {
        const { req, res } = context

        const { pathname } = new URL(req.url as string, URLS.VIVIDSEATS)
        const webPath = webPathOverride || pathname

        const initialSubCategoryParams: SubCategoryParams = { categoryId, webPath }
        let initialSubCategory: CmsSubCategory | null = null

        let initialSubCategoryPerformersParams: PerformerParams = {
            categoryId,
            pageSize: SUB_CATEGORY_FEATURED_EVENTS_PAGE_SIZE,
            sortBy: SUB_CATEGORY_FEATURED_EVENTS_SORT_BY,
        }

        let initialSubCategoryPerformersData: PerformerDataType | null = null

        const performersDataByLinkGroups: LinkGroupPerformerData[] = []
        const cardWithImageGroupsData: CardWithImageGroupsData[] = []

        try {
            // Fetch sub-category
            initialSubCategory = await serverSideReadSubCategory(initialSubCategoryParams)

            // Fetch sub-category performers
            initialSubCategoryPerformersParams = {
                ...initialSubCategoryPerformersParams,
                subCategoryId: initialSubCategory.id,
            }

            initialSubCategoryPerformersData = await serverSideReadPerformers(initialSubCategoryPerformersParams)

            for (let i = 0; i < initialSubCategory.linkGroups?.length; i++) {
                const linkGroup = initialSubCategory.linkGroups[i]
                if (linkGroup.type === 'vivid.fixed-link-group') {
                    if (linkGroup?.title && linkGroup?.links?.length) {
                        performersDataByLinkGroups.push({ title: linkGroup?.title, links: linkGroup?.links })
                    }
                } else {
                    const filterParameters = {
                        categoryId,
                        subcategoryId: initialSubCategory?.id,
                        ...linkGroup.filterParameters,
                    }

                    const performersData = await serverSideReadPerformers(filterParameters)
                    const performersLinks = performersData?.items
                        ?.filter((item) => !!item.webPath && !!item.name)
                        .map(({ name: label, webPath: url, id }) => ({
                            label,
                            url,
                            id,
                        })) as PerformerLink[]

                    if (performersLinks.length) {
                        performersDataByLinkGroups.push({ title: linkGroup.title, links: performersLinks })
                    }
                }
            }

            for (let i = 0; i < initialSubCategory.cardWithImageGroups?.length; i++) {
                const cardGroup = initialSubCategory.cardWithImageGroups[i]
                const { title, subtitle, filterParameters, ctaLink, ctaTitle } = cardGroup
                const productionData = await serverSideReadProductionList(filterParameters)
                const productions = productionData?.items
                const cta = ctaTitle && ctaLink ? { link: ctaLink, title: ctaTitle } : null
                cardWithImageGroupsData.push({
                    title,
                    subtitle,
                    cta,
                    productions,
                })
            }
        } catch (e) {
            console.error((e as Error).message)
            if ([400, 404].includes(((e as AxiosError).response as AxiosResponse).status)) {
                res.writeHead(302, 'Not found', { Location: `/404?sub-category=${webPath}` })
                res.end()
                return {
                    props: {
                        initialSubCategoryParams,
                        initialSubCategory,
                        initialSubCategoryPerformersParams,
                        initialSubCategoryPerformersData,
                        performersDataByLinkGroups,
                        cardWithImageGroupsData,
                    },
                }
            }
        }

        return {
            props: {
                initialSubCategoryParams,
                initialSubCategory,
                initialSubCategoryPerformersParams,
                initialSubCategoryPerformersData,
                performersDataByLinkGroups,
                cardWithImageGroupsData,
            },
        }
    }

    return getServerSideProps
}

export const getMetadataForSubCategory = (subCategory: CmsSubCategory | undefined): Metadata | undefined => {
    if (!subCategory) {
        return undefined
    }

    const {
        name: subCategoryName,
        webPath,
        mobileImageUrl,
        desktopImageUrl,
        meta: { title: metaTitle, description: metaDescription, keywords: metaKeywords },
    } = subCategory

    const metadata: Metadata = {
        title: replaceCmsTokens(metaTitle, { subCategory }) || subCategoryName,
        description: replaceCmsTokens(metaDescription, { subCategory }),
        keywords: replaceCmsTokens(metaKeywords, { subCategory }),
        url: `${URLS.VIVIDSEATS}${webPath}`,
        canonicalEnabled: true,
        trailingSlashBehavior: 'never',
        image: mobileImageUrl || desktopImageUrl,
    }

    return metadata
}
